/**
 * `/hire-a-property-manager` — a static marketing page, not admin-editable
 * content like CompliancePage.tsx's four stub pages. There's no firm-supplied
 * copy for this yet (same disclosed-placeholder posture as the compliance
 * pages and the lease/notice generators), so the body below is generic
 * property-management copy the firm can ask to have revised, plus the one
 * hard requirement: the phone number as a prominent call-or-text CTA.
 *
 * Redesigned per direct request for something more visually dynamic than
 * the original `.public__compliance`-stub treatment (a plain eyebrow + h1 +
 * bullet list). Same information as before, presented as three deliberate
 * moves rather than one static column:
 *   1. A full-bleed teal gradient hero band carries the headline in
 *      --public-hero-display (Google Sans) — the same "confident band"
 *      language the homepage hero and listing-detail hero already use for
 *      this site's other high-stakes moments, instead of a small tracked-
 *      caps eyebrow tag (dropped entirely; the band does that job now).
 *   2. The service list becomes a two-column "what we handle" section —
 *      a sticky label on the left, icon rows on the right — since these
 *      seven items are parallel offerings, not a sequence, so a numbered
 *      list or a fourth reuse of the gradient-border card (already used by
 *      listing cards, the homepage bento, and the phone CTA below) would
 *      both be the wrong structural device. Circular teal icon badges are
 *      a treatment unique to this page.
 *   3. The phone CTA card's structure (`.public__hire-pm-phone` and its
 *      children) is shared with HomeBento.tsx's copy of the same card, per
 *      the original direct instruction to leave it in place. Per a later
 *      request scoped explicitly to this page, its number and caption are
 *      sized up and the caption rewritten to sell the call rather than just
 *      describe it — both scoped under `.public__hire-pm` in styles.css so
 *      HomeBento.tsx's smaller, plainer copy of the card is untouched.
 *
 * A fourth addition, underneath the phone card: HirePropertyManagerInquiryForm,
 * a simple inquiry form for owners who'd rather write than call — see its
 * own doc comment for what it deliberately isn't (the tenant-facing
 * inquiry/tour funnel CLAUDE.md describes elsewhere).
 */

import {
  Banknote,
  ClipboardCheck,
  ClipboardList,
  FileChartColumn,
  Megaphone,
  Phone,
  ShieldCheck,
  Wrench,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { useBrand } from '../../lib/brand.js';
import { HirePropertyManagerInquiryForm } from './HirePropertyManagerInquiryForm.js';

export const PHONE_DISPLAY = '866-725-2249';
export const PHONE_HREF = 'tel:+18667252249';

const SERVICES: { icon: LucideIcon; label: string }[] = [
  { icon: Megaphone, label: 'Marketing and leasing of vacant units' },
  { icon: ClipboardCheck, label: 'Tenant screening and lease preparation' },
  { icon: Banknote, label: 'Rent collection and owner disbursement' },
  { icon: Wrench, label: 'Maintenance coordination with vetted vendors' },
  { icon: ClipboardList, label: 'Move-in and move-out inspections' },
  { icon: ShieldCheck, label: 'Michigan-compliant notices and recordkeeping' },
  { icon: FileChartColumn, label: 'Owner statements and reporting' },
];

export function HirePropertyManager() {
  const { company } = useBrand();

  return (
    <article className="public__hire-pm">
      <section className="public__hire-pm-hero">
        <div className="public__hire-pm-hero-inner">
          <h1>Hire a Property Manager</h1>
        </div>
      </section>

      <div className="public__hire-pm-body">
        <p className="public__hire-pm-intro">
          {company.name} manages rental properties throughout metro Detroit for owners who want
          their investment handled professionally — from finding a qualified tenant to keeping the
          property compliant with Michigan law, we cover the day-to-day so you don&apos;t have to.
        </p>

        <div className="public__hire-pm-services">
          <div className="public__hire-pm-services-label">
            <h2>What we handle</h2>
            <p className="muted">Everything it takes to run a rental, handled.</p>
          </div>
          <ul className="public__hire-pm-services-list">
            {SERVICES.map(({ icon: Icon, label }) => (
              <li key={label}>
                <span className="public__hire-pm-services-icon">
                  <Icon aria-hidden="true" />
                </span>
                <span>{label}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className="public__hire-pm-phone">
          <Phone className="public__hire-pm-phone-icon" aria-hidden="true" />
          <div>
            <a className="public__hire-pm-phone-number" href={PHONE_HREF}>
              {PHONE_DISPLAY}
            </a>
            <p className="muted">
              Talk to a property manager today — no pressure, no obligation. Call or text us and
              we&apos;ll walk you through exactly how we can help you with your rental needs.
            </p>
          </div>
        </div>

        <HirePropertyManagerInquiryForm />
      </div>
    </article>
  );
}
